import { type ReactElement, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { formatCurrencyForDisplay } from 'Currency';
import { MailtoLink } from 'MailtoLink';
import { Radio, RadioGroup } from 'Radio';
import { Text } from 'Text';
import scholarshipRibbon from 'vectors/scholarship_ribbon.svg';
import { type CohortDates, type CohortRegistrationOption } from '../TuitionAndRegistration.types';
import { LOCALE_PREFIX } from '../constants';

/*
    The width of the register button and of the column it sits in. 210px is not on our scale, so 208px.
*/
export const REGISTRATION_ACTION_WIDTH = 'sm:w-52';

/*
    The presentational pieces the three registration screens share.
*/

/*
    The card every registration screen sits in.
    `overflow-hidden` clips the ledger, the deadline strip and the footer panel, which all bleed past
    the card padding with `-mx-5`.
*/
export function RegistrationCard({
    children,
    flushTop = false,
}: {
    children: ReactNode;
    // Drops the top padding for an offer with nothing above the ledger.
    flushTop?: boolean;
}): ReactElement {
    return (
        <div
            className={twMerge(
                'my-5 overflow-hidden rounded-2 border border-slate-grey-200 p-5 text-start',
                flushTop && 'pt-0',
            )}
        >
            {children}
        </div>
    );
}

/*
    The grey panel at the foot of the card, which holds the plan choices and the register button.
*/
export function RegistrationFooterPanel({ children }: { children: ReactNode }): ReactElement {
    return <div className="-mx-5 -mb-5 bg-slate-grey-50 p-5">{children}</div>;
}

/*
    The top of the card: the copy on the left and the scholarship total on the right, centred against
    each other. Reversed on desktop so the copy reads first while the DOM keeps the total first,
    which is the order mobile wants.
*/
export function RegistrationHeader({ children }: { children: ReactNode }): ReactElement {
    return (
        <div className="flex flex-col gap-5 sm:flex-row-reverse sm:items-center sm:justify-between sm:gap-6">
            {children}
        </div>
    );
}

/*
    The two columns inside the footer: what the learner chooses on the left, and the register button
    on the right.
*/
export function RegistrationFooterColumns({
    children,
    actions,
}: {
    children?: ReactNode;
    actions: ReactNode;
}): ReactElement {
    return (
        <div className="flex flex-col gap-6 sm:flex-row sm:justify-between">
            <div className="flex-1">{children}</div>
            {/* On mobile the actions join the outer column, so the start dates can go first and the button last. */}
            <div className={`flex flex-col gap-5 max-sm:contents sm:shrink-0 ${REGISTRATION_ACTION_WIDTH}`}>
                {actions}
            </div>
        </div>
    );
}

type RegistrationGraphicProps = {
    titleKey?: string | null;
    subtitleKey?: string | null;
    scholarshipAmount: number | null | undefined;
    numOfferedScholarships: number;
};

/*
    The scholarship total beside the rosette, which sits top right of the card on desktop and above
    the copy on mobile.
*/
export function RegistrationGraphic({
    titleKey,
    subtitleKey,
    scholarshipAmount,
    numOfferedScholarships,
}: RegistrationGraphicProps): ReactElement | null {
    const { t } = useTranslation('back_royal');

    if (!titleKey && !subtitleKey) return null;

    const totalScholarship = formatCurrencyForDisplay(scholarshipAmount ?? undefined);

    return (
        <div className="flex shrink-0 items-start gap-3">
            {/* Empty alt: the amount beside it carries the meaning. */}
            <img src={scholarshipRibbon} alt="" width={40} height={54} className="shrink-0" />
            <div>
                {titleKey ? (
                    <Text as="div" className="text-md text-grey-darker">
                        {/* The title wraps the amount in <strong>, so it needs Trans rather than t(). */}
                        <Trans
                            t={t}
                            i18nKey={titleKey}
                            values={{ totalScholarship }}
                            components={{ strong: <strong className="text-3xl font-semibold" /> }}
                        />
                    </Text>
                ) : null}
                {subtitleKey ? (
                    <Text as="div" className="text-xs text-slate-grey-400">
                        {t(subtitleKey, { num: numOfferedScholarships })}
                    </Text>
                ) : null}
            </div>
        </div>
    );
}

type RegistrationNarrativeProps = {
    tuitionDetailsLabelKey?: string | null;
    narrativeKey?: string | null;
    numOfferedScholarships: number;
    children?: ReactNode;
};

/*
    The heading sits above the copy on desktop and below it on mobile, with one heading rather than two.
*/
export function RegistrationNarrative({
    tuitionDetailsLabelKey,
    narrativeKey,
    numOfferedScholarships,
    children,
}: RegistrationNarrativeProps): ReactElement {
    const { t } = useTranslation('back_royal');

    return (
        <div>
            <div className="flex flex-col-reverse sm:flex-col">
                {tuitionDetailsLabelKey ? (
                    <Text as="h3" variant="h3" className="mb-0 mt-5 text-xs text-grey-darker sm:mb-2 sm:mt-0">
                        {t(tuitionDetailsLabelKey)}
                    </Text>
                ) : null}

                {narrativeKey ? (
                    <Text as="div" className="text-xs leading-5.5 text-slate-grey">
                        {t(narrativeKey, { num: numOfferedScholarships })}
                    </Text>
                ) : null}
            </div>

            {children}
        </div>
    );
}

type RegistrationDateProps = {
    dates: CohortDates;
    isExecEd: boolean;
    admissionsEmail: string;
    className?: string;
};

/*
    The amber strip that spans the card. The parent passes the negative margin that bleeds it past the
    card padding.
*/
export function RegistrationDateStrip({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div
            className={`border-y border-yellow bg-yellow/50 px-5 py-2.5 text-center text-xs text-grey-darker ${className ?? ''}`}
        >
            {children}
        </div>
    );
}

/* Each line has a desktop and a mobile variant, because the mobile locale keys drop the year. */
export function RegistrationDate({ dates, isExecEd, admissionsEmail, className }: RegistrationDateProps): ReactElement {
    const { t } = useTranslation('back_royal');
    const showPreferLater = isExecEd && !dates.beforeRegistrationDeadline;

    return (
        <RegistrationDateStrip className={className}>
            <span className="max-sm:hidden">
                {t(`${LOCALE_PREFIX}.registration_deadline_no_start_date`, {
                    registrationDeadline: dates.registrationDeadline,
                })}
            </span>
            <span className="sm:hidden">
                {t(`${LOCALE_PREFIX}.registration_deadline_mobile_no_start_date`, {
                    registrationDeadline: dates.registrationDeadlineMobile,
                })}
            </span>

            {showPreferLater ? (
                <>
                    {' '}
                    <span className="max-sm:hidden">
                        <Trans
                            t={t}
                            i18nKey={`${LOCALE_PREFIX}.prefer_later_start_date`}
                            components={{
                                mail: <MailtoLink className="text-xs hover:text-primary" email={admissionsEmail} />,
                            }}
                        />
                    </span>
                    <span className="sm:hidden">{t(`${LOCALE_PREFIX}.prefer_later_start_date_mobile`)}</span>
                </>
            ) : null}

            {!dates.inEarlyAdmissionsRegistrationPeriod && dates.beforeRegistrationDeadline ? (
                <>
                    {' '}
                    <Trans
                        t={t}
                        i18nKey={`${LOCALE_PREFIX}.confirm_message_before_deadline`}
                        values={{ registrationOpen: dates.registrationOpenDate }}
                    />
                </>
            ) : null}
        </RegistrationDateStrip>
    );
}

type StartDateOptionsProps = {
    options: CohortRegistrationOption[];
    isDisabled: boolean;
};

// The multi-cohort start-date picker, shown when the offer allows registering into other cohorts.
export function StartDateOptions({ options, isDisabled }: StartDateOptionsProps): ReactElement {
    const { t } = useTranslation('back_royal');

    return (
        <div>
            <Text as="div" weight="semibold" className="mb-3 text-xs text-grey-darker">
                {t(`${LOCALE_PREFIX}.choose_start_date`)}
            </Text>
            <RadioGroup name="registerForCohortId" color="success" isDisabled={isDisabled}>
                {options.map(option => (
                    <Radio key={option.cohortId} value={option.cohortId}>
                        <span className="text-xs font-medium text-grey-darker">{option.date}</span>
                    </Radio>
                ))}
            </RadioGroup>
        </div>
    );
}
